// src/components/Purchase/PurchaseOrders.jsx
/**
 * PurchaseOrders.jsx (FEDIS - Bons de commande / BC)
 * ==================================================
 * Rôle:
 * - Affiche la liste des bons de commande (BC) + filtres
 * - Permet de créer un nouveau BC (brouillon ou validé)
 * - Permet d'ouvrir le détail d'un BC (route: /purchases/orders/:id)
 * - Permet de télécharger le PDF d'un BC
 *
 * Points clés:
 * - Un BC = intention d'achat (le stock NE BOUGE PAS).
 * - La TVA est configurable par BC (tva_pct envoyé au backend).
 * - Conversion en BA (réception / bon d’achat) se fait depuis la page détail (PurchaseOrderView).
 *
 * Dépendances:
 * - API: /api/purchase-orders (GET/POST), /api/purchase-orders/:id/pdf
 * - Routing: ce composant doit être rendu sous /purchases/orders
 */

import React, { useEffect, useMemo, useState } from 'react'
import { apiFetch, apiFetchBlob } from '../../api.js'
import { useNavigate } from 'react-router-dom'

const money = (v) => Number(v ?? 0).toLocaleString('fr-FR')

function forceDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

function StatusBadge({ status }) {
  const s = (status || 'draft').toLowerCase()
  const map = {
    draft: 'bg-surface-hover text-content-primary border-border',
    approved: 'bg-slate-50 text-slate-800 border-slate-100',
    sent: 'bg-slate-50 text-slate-800 border-slate-100',
    partially_received: 'bg-amber-50 text-amber-800 border-amber-100',
    received: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    cancelled: 'bg-rose-50 text-rose-700 border-rose-100',
  }
  const label = {
    draft: 'Brouillon',
    approved: 'Validé',
    sent: 'Envoyé',
    partially_received: 'Reçu partiel',
    received: 'Reçu complet',
    cancelled: 'Annulé',
  }
  return (
    <span className={`text-[11px] px-2 py-1 rounded-full border ${map[s] || map.draft}`}>
      {label[s] || status || '—'}
    </span>
  )
}

export default function PurchaseOrders({ products = [] }) {
  const navigate = useNavigate()

  // List
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])

  // Filters
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  // Create form
  const [supplierName, setSupplierName] = useState('')
  const [supplierContact, setSupplierContact] = useState('')
  const [supplierPhone, setSupplierPhone] = useState('')
  const [tvaPct, setTvaPct] = useState(18)

  const [pid, setPid] = useState('')
  const [qty, setQty] = useState(1)
  const [cost, setCost] = useState('')
  const [lines, setLines] = useState([])
  const [saving, setSaving] = useState(false)

  const inputBase =
    'w-full border border-border bg-surface-card rounded-lg px-3 py-2 text-sm outline-none transition focus:ring-2 focus:ring-slate-200 focus:border-slate-300'
  const labelBase = 'text-xs font-medium text-content-secondary'
  const cardBase = 'bg-surface-card border border-border rounded-xl shadow-sm'

  const totalHT = useMemo(
    () => lines.reduce((s, l) => s + Number(l.qty) * Number(l.unit_cost), 0),
    [lines]
  )

  const selectedProduct = useMemo(
    () => products.find(x => String(x.id) === String(pid)),
    [products, pid]
  )

  function addLine() {
    const p = products.find(x => String(x.id) === String(pid))
    if (!p) return alert('Choisir un produit')
    if (qty <= 0) return alert('Qté invalide')
    if (cost === '' || Number(cost) < 0) return alert('Coût unitaire invalide')

    setLines(l => [
      ...l,
      {
        id: Date.now() + Math.random(),
        product_id: p.id,
        product_name: p.name,
        qty: Number(qty),
        unit_cost: Number(cost),
      },
    ])

    setPid('')
    setQty(1)
    setCost('')
  }

  function removeLine(id) {
    setLines(l => l.filter(x => x.id !== id))
  }

  async function reload() {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (q) params.set('q', q)
      if (status) params.set('status', status)
      if (from) params.set('from', from)
      if (to) params.set('to', to)

      const data = await apiFetch(`/api/purchase-orders?${params.toString()}`)
      setRows(Array.isArray(data) ? data : [])
    } catch (e) {
      alert('Erreur chargement BC: ' + e.message)
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { reload() }, []) // eslint-disable-line

  async function createPO({ validateNow = false } = {}) {
    if (!supplierName) return alert('Fournisseur requis')
    if (lines.length === 0) return alert('Ajoute au moins un article')

    const tva = Number(tvaPct ?? 18)
    if (!Number.isFinite(tva) || tva < 0 || tva > 100) return alert('TVA invalide (0-100)')

    setSaving(true)
    try {
      const payload = {
        supplier_name: supplierName,
        supplier_phone: supplierPhone || null,
        supplier_contact: supplierContact || null,
        tva_pct: tva,
        lines: lines.map(l => ({
          product_id: l.product_id,
          product_name: l.product_name,
          qty_ordered: l.qty,
          unit_cost_expected: l.unit_cost,
        })),
      }

      const data = await apiFetch('/api/purchase-orders', {
        method: 'POST',
        body: payload,
      })

      if (validateNow && (data?.id || data?.external_id)) {
        // backend accepte uuid OU external_id
        const ref = data.id || data.external_id
        await apiFetch(`/api/purchase-orders/${ref}/validate`, { method: 'POST' })
      }

      // reset
      setSupplierName('')
      setSupplierContact('')
      setSupplierPhone('')
      setTvaPct(18)
      setPid('')
      setQty(1)
      setCost('')
      setLines([])

      await reload()
      alert(validateNow ? 'Bon de commande créé + validé ✅' : 'Bon de commande créé ✅')
    } catch (e) {
      alert('Erreur création BC: ' + e.message)
    } finally {
      setSaving(false)
    }
  }

  async function downloadPOPDF(po) {
    try {
      if (!po?.id) return alert('id manquant')
      const blob = await apiFetchBlob(`/api/purchase-orders/${po.id}/pdf`)
      forceDownload(blob, `BC_${po.external_id || po.id}.pdf`)
    } catch (e) {
      alert('PDF BC impossible: ' + e.message)
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl font-extrabold text-content-primary">Bons de commande</h1>
          <p className="text-sm text-content-secondary mt-1">
            Prépare une commande fournisseur, puis convertis-la en bon d’achat à la réception.
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs text-content-secondary">Total (lignes BC en cours)</div>
          <div className="text-2xl font-extrabold text-content-primary">{money(totalHT)} XOF</div>
          <div className="text-xs text-content-muted mt-1">{lines.length} ligne(s)</div>
        </div>
      </div>

      {/* Create */}
      <div className={`${cardBase} p-5 mb-6`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-content-primary">Nouveau bon de commande</h2>
          <span className="text-[11px] px-2 py-1 rounded-full bg-surface-primary text-content-secondary border border-border">
            BC = prévision (stock ne bouge pas)
          </span>
        </div>

        {/* Supplier */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <div className={labelBase}>Nom fournisseur *</div>
            <input value={supplierName} onChange={e => setSupplierName(e.target.value)} className={inputBase} placeholder="ex: Fournisseur X" />
          </div>
          <div>
            <div className={labelBase}>Contact</div>
            <input value={supplierContact} onChange={e => setSupplierContact(e.target.value)} className={inputBase} placeholder="Nom du contact" />
          </div>
          <div>
            <div className={labelBase}>Téléphone</div>
            <input value={supplierPhone} onChange={e => setSupplierPhone(e.target.value)} className={inputBase} placeholder="+226…" />
          </div>
          <div>
            <div className={labelBase}>TVA (%)</div>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={tvaPct}
              onChange={e => setTvaPct(Number(e.target.value))}
              className={inputBase}
            />
          </div>
        </div>

        {/* Lines */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-6">
            <div className={labelBase}>Produit</div>
            <select value={pid} onChange={e => setPid(e.target.value)} className={inputBase}>
              <option value="">-- choisir produit --</option>
              {products.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} · stock {p.stock_quantity ?? p.stock ?? '—'}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <div className={labelBase}>Quantité</div>
            <input type="number" min="1" value={qty} onChange={e => setQty(Number(e.target.value))} className={inputBase} />
          </div>
          <div className="md:col-span-2">
            <div className={labelBase}>PU attendu (HT)</div>
            <input type="number" min="0" value={cost} onChange={e => setCost(e.target.value)} className={`${inputBase} text-right`} placeholder="ex: 1200" />
          </div>
          <div className="md:col-span-2">
            <button
              onClick={addLine}
              className="w-full px-4 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 active:scale-[0.99] transition"
            >
              Ajouter
            </button>
          </div>
        </div>

        {selectedProduct && (
          <div className="mt-3 text-xs px-3 py-2 rounded-lg bg-surface-primary border border-border text-content-secondary">
            Produit sélectionné: <b className="text-content-primary">{selectedProduct.name}</b>
          </div>
        )}

        {/* Lines preview */}
        <div className="mt-5 overflow-auto border border-border rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary text-content-secondary">
              <tr>
                <th className="p-3 text-left font-semibold">Produit</th>
                <th className="p-3 text-right font-semibold">Qté</th>
                <th className="p-3 text-right font-semibold">PU (HT)</th>
                <th className="p-3 text-right font-semibold">Total</th>
                <th className="p-3 text-right font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-content-secondary">
                    Ajoute des articles pour créer le bon de commande.
                  </td>
                </tr>
              ) : (
                lines.map((l, idx) => (
                  <tr
                    key={l.id}
                    className={`border-t border-border-light ${idx % 2 === 0 ? 'bg-surface-card' : 'bg-surface-primary/40'} hover:bg-slate-50/40 transition`}
                  >
                    <td className="p-3 font-medium text-content-primary">{l.product_name}</td>
                    <td className="p-3 text-right font-semibold">{l.qty}</td>
                    <td className="p-3 text-right">{money(l.unit_cost)} XOF</td>
                    <td className="p-3 text-right font-extrabold">{money(l.qty * l.unit_cost)} XOF</td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => removeLine(l.id)}
                        className="px-3 py-1.5 rounded-lg text-sm font-semibold text-red-600 hover:bg-red-50 border border-transparent hover:border-red-100 transition"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Actions */}
        <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-end">
          <button
            onClick={() => createPO({ validateNow: false })}
            disabled={saving}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${
              saving ? 'bg-surface-hover text-content-muted border-border cursor-not-allowed'
                     : 'bg-surface-card text-content-primary border-border hover:bg-surface-primary'
            }`}
          >
            {saving ? 'Création…' : 'Créer en brouillon'}
          </button>

          <button
            onClick={() => createPO({ validateNow: true })}
            disabled={saving}
            className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition ${
              saving ? 'bg-slate-300 cursor-not-allowed'
                     : 'bg-slate-900 hover:bg-slate-950 active:scale-[0.99]'
            }`}
          >
            {saving ? 'Création…' : 'Créer + Valider'}
          </button>
        </div>
      </div>

      {/* Filters + List */}
      <div className="bg-surface-card border border-border rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="font-bold text-content-primary">Historique des bons de commande</div>
            <div className="text-xs text-content-secondary">Ouvre un BC pour le valider ou le convertir en achat.</div>
          </div>
          <div className="text-xs text-content-secondary">{loading ? 'Chargement…' : `${rows.length} BC`}</div>
        </div>

        <div className="p-5 grid gap-3 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className={labelBase}>Recherche</div>
            <input value={q} onChange={e => setQ(e.target.value)} className={inputBase} placeholder="Fournisseur, external_id…" />
          </div>
          <div className="md:col-span-3">
            <div className={labelBase}>Statut</div>
            <select value={status} onChange={e => setStatus(e.target.value)} className={inputBase}>
              <option value="">Tous</option>
              <option value="draft">Brouillon</option>
              <option value="approved">Validé</option>
              <option value="sent">Envoyé</option>
              <option value="partially_received">Reçu partiel</option>
              <option value="received">Reçu complet</option>
              <option value="cancelled">Annulé</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <div className={labelBase}>Du</div>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} className={inputBase} />
          </div>
          <div className="md:col-span-2">
            <div className={labelBase}>Au</div>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} className={inputBase} />
          </div>
          <div className="md:col-span-1 flex items-end">
            <button
              onClick={reload}
              className="w-full px-3 py-2 rounded-lg text-sm font-semibold border border-border hover:bg-surface-primary"
            >
              Filtrer
            </button>
          </div>
        </div>

        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary text-content-secondary">
              <tr>
                <th className="p-3 text-left font-semibold">BC</th>
                <th className="p-3 text-left font-semibold">Fournisseur</th>
                <th className="p-3 text-left font-semibold">Statut</th>
                <th className="p-3 text-right font-semibold">Total HT</th>
                <th className="p-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-content-secondary">
                    Aucun bon de commande.
                  </td>
                </tr>
              ) : (
                rows.map((po, idx) => (
                  <tr
                    key={po.id || po.external_id || idx}
                    className={`border-t border-border-light ${idx % 2 === 0 ? 'bg-surface-card' : 'bg-surface-primary/40'} hover:bg-slate-50/30 transition`}
                  >
                    <td className="p-3">
                      <button
                        onClick={() => navigate(`/purchases/orders/${po.id}`)}
                        className="font-semibold text-content-primary hover:text-slate-800"
                        title="Ouvrir"
                      >
                        {po.external_id || po.id}
                      </button>
                      <div className="text-xs text-content-secondary">
                        {po.created_at ? String(po.created_at).slice(0, 10) : ''}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-content-primary">{po.supplier_name}</div>
                      <div className="text-xs text-content-secondary">{po.supplier_phone || '—'}</div>
                    </td>
                    <td className="p-3">
                      <StatusBadge status={po.status} />
                    </td>
                    <td className="p-3 text-right font-extrabold text-content-primary">
                      {money(po.total_ht ?? po.total ?? 0)} XOF
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => downloadPOPDF(po)}
                          className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-border hover:bg-surface-primary"
                        >
                          PDF
                        </button>
                        <button
                          onClick={() => navigate(`/purchases/orders/${po.id}`)}
                          className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-slate-900 hover:bg-black"
                        >
                          Ouvrir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
